import type { TicketViewType } from '@interfaces/ticketevolution/tikets';
import type { FlightSearchFormValues } from '@v2/views/flights/components/Header/use-flight-search-form';
import type { PreBookingPayload } from '@v2/views/flights/components/FlightsContent/FlightDrawer/FlightFares';
import type { Flight } from '@v2/views/flights/types';

export enum ShoppingCartStepEnum {
  BUNDLE_TYPE_SELECTION = 'BUNDLE_TYPE_SELECTION',
  TICKET_SELECTION = 'TICKET_SELECTION',
  HOTEL_SELECTION = 'HOTEL_SELECTION',
  CHECKOUT = 'CHECKOUT',
  COMPLETED = 'COMPLETED',
}

export enum ShoppingCartTypeEnum {
  SHOPPING_CART = 'SHOPPING_CART',
  QUOTATION = 'QUOTATION',
}

export interface CheckoutPayload {
  id?: number;
  userId?: string | number | null;
  orderId?: number;
  packageConfigDetails?: PackageDetails | null;
  selectedHotelDetails?: HotelDetails | null;
  selectedTicketDetails?: TicketDetails | null;
  currentStep?: ShoppingCartStepEnum;
  type?: ShoppingCartTypeEnum;
  bonusMarkupType?: 'fixed' | 'percentage' | null;
  bonusMarkupValue?: number | null;
  refundableAccepted?: boolean | null;
  refundablePrice?: number | null;
  flightDetails?: {
    fare: PreBookingPayload;
    searchParams: FlightSearchFormValues & {
      flightsSelected: Flight[];
    };
  } | null;
}

export interface PackageDetails {
  isBundle: boolean;
  tickets: number;
  currency: string;
  numOfNights: number;
  hotelStartDate: string;
  hotelEndDate: string;
  roomConfigurations: RoomConfiguration[];
  selected: {
    hotel: boolean | undefined;
    tickets: boolean | undefined;
  };
  eventDate: string;
  // How eventDate was resolved — 'utc' means the venue-local start time was unavailable
  // and the order should be flagged for an ops event-date review.
  eventDateSource?: 'local' | 'utc' | 'none';
  eventName: string;
  location: LocationDetails;
  performers: Performer[];
  timeZone: string;
  totalPrice: number;
  eventLink: string;
  refundableAccepted?: boolean | null;
  refundablePrice?: number | null;
}

interface RoomConfiguration {
  adults: number;
  children: any[]; // Array can be further typed if required
  infants: number;
}

interface LocationDetails {
  id: string;
  name: string;
  country_name: string;
  city_name: string;
  latitude: number;
  longitude: number;
}

interface Performer {
  name: string;
  isPrimary: boolean;
}

interface HotelDetails {
  id: string;
  name: string;
  location: AddressDetails;
  rating: number;
  guestRating?: number | null;
  guestRatingCount?: number | null;
  trustSignals?: string[] | null;
  mandatoryFees?: string | null;
  optionalFees?: string | null;
  rooms: number;
  registrationTime: RegistrationTime;
  price: number;
  roomSelections: RoomSelection[];
  postalCode: string;
  city: string;
  country: string;
  phone: string;
  image: string;
  fullAddress: string;
}

interface AddressDetails {
  line1: string;
  city: string;
  stateCode: string;
  stateName: string;
  postalCode: string;
  countryCode: string;
}

interface RegistrationTime {
  checkIn: string;
  checkOut: string;
}

interface RoomSelection {
  roomId: number;
  rateId: number;
  occupancy: string;
  price: number;
  description: string;
  name: string;
  refundable?: boolean;
}

export interface TicketDetails {
  price: number;
  wholesalePrice: number;
  retailPrice: number;
  tax: number;
  tax_signature: string;
  ticket_group_id: number;
  quantity: number;
  seat_order: string;
  ticket_group_signature: string;
  stadiumName: string;
  section: string;
  row: string;
  publicNotes: string;
  viewType?: TicketViewType | null;
  type: string;
  venueId?: string | number;
  configurationId?: string | number;
  tevoSectionName?: string;
}
